import { produce } from "immer";

import { SearchSortAttribute } from "../api/adsApi/adsApi.interfaces";
import {
    defaultContentSubTypeForInsight,
    defaultContentSubTypeForInsightInUniversalView,
    defaultContentSubTypeForMarketingContent,
    initialFilterState,
} from "../config/filters.config";
import { allPackagedInsightScope } from "../constants/filters";
import type { Item } from "../types/common";
import type {
    DiscoveryFilter,
    DiscoveryFilterField,
    DiscoveryFilters,
    DiscoveryFilterState,
    DiscoveryInsightsFilters,
    DiscoveryMarketingContentFilters,
} from "../types/filters/common";
import { FieldToTypeMap } from "../types/filters/common";
import type { IHubContentSubType } from "../types/ihubContentType";
import { IHubContentType } from "../types/ihubContentType";
import { IHubContentProvider } from "../types/providers";
import { getProcessedInitialFilterState } from "../utils/filters.utils";
import { isAccountAgnosticId } from "../utils/permission.utils";

import { zustandStore } from "./zustand.helpers";

export interface DiscoveryFilterActions {
    setContentType: (contentType: IHubContentType) => void;
    getContentType: () => IHubContentType;
    setContentSubType: (
        contentType: IHubContentType,
        contentSubType: IHubContentSubType,
    ) => void;
    getContentSubType: (
        currentContentType?: IHubContentType,
    ) => IHubContentSubType;
    getContentSubTypeProvider: () => IHubContentProvider;
    setTypeOptions: (provider: IHubContentProvider, options: string[]) => void;
    getTypeOptions: (provider: IHubContentProvider) => string[];
    resetTypeOptions: (provider: IHubContentProvider) => void;
    setTypes: (
        contentType: IHubContentType,
        contentSubType: IHubContentSubType,
    ) => void;
    resetTypes: () => void;
    clearFieldSelections: (field: DiscoveryFilterField) => void;
    setFieldSelections: (field: DiscoveryFilterField, items: Item[]) => void;
    setField: (
        field: DiscoveryFilterField,
        item: Item,
        makeItVisible?: boolean,
        allowMultiSelect?: boolean,
    ) => void;
    getInsightsFilters: () => DiscoveryInsightsFilters;
    getMarketingContentFilters: () => DiscoveryMarketingContentFilters;
    getFilters: (contentType?: IHubContentType) => DiscoveryFilters;
    setFilterOptions: (field: DiscoveryFilterField, items: Item[]) => void;
    setData: (type: IHubContentProvider, data: any) => void;
    setSearchQuery: (query: string) => void;
    resetSearchQueryAndSortAttribute: () => void;
    addFilter: (filter: DiscoveryFilter) => void;
    removeFilter: (filter: DiscoveryFilter) => void;
    hasAppliedFilters: (contentSubType?: IHubContentSubType) => boolean;
    resetState: () => void;
    resetFilters: () => void;
    setSortAttribute: (sortAttribute: string) => void;
    setScopeToLabel: (scopeToLabel: { [key: string]: string }) => void;
}

export const { store: discoveryFilterStore } = zustandStore<
    DiscoveryFilterState,
    DiscoveryFilterActions
>("DiscoveryFilters", getProcessedInitialFilterState(), (set, get) => ({
    setContentType: (contentType: IHubContentType) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                draft.contentType = contentType;
                return draft;
            });
        });
    },
    getContentType: () => {
        const state: DiscoveryFilterState = get();
        return state.contentType;
    },
    setContentSubType: (
        contentType: IHubContentType,
        contentSubType: IHubContentSubType,
    ) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                draft[contentType].contentSubType = contentSubType;
                return draft;
            });
        });
    },
    getContentSubType: (currentContentType?: IHubContentType) => {
        const state: DiscoveryFilterState = get();
        return state[currentContentType ?? state.contentType].contentSubType;
    },
    getContentSubTypeProvider: () => {
        const state: DiscoveryFilterState = get();
        return state[state.contentType].contentSubType.provider;
    },
    resetTypeOptions: (provider: IHubContentProvider) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                draft[state.contentType].filters[provider].typeOptions =
                    initialFilterState[state.contentType].filters[
                        provider
                    ].typeOptions;
                return draft;
            });
        });
    },
    setTypeOptions: (provider: IHubContentProvider, options: string[]) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                draft[state.contentType].filters[provider].typeOptions =
                    options;
                return draft;
            });
        });
    },
    getTypeOptions: (provider: IHubContentProvider) => {
        const state: DiscoveryFilterState = get();
        return state[state.contentType].filters[provider]?.typeOptions ?? [];
    },
    setTypes: (
        contentType: IHubContentType,
        contentSubType: IHubContentSubType,
    ) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                // set type to new value
                draft.contentType = contentType;
                draft[contentType].contentSubType = contentSubType;
                return draft;
            });
        });
    },
    resetTypes: () => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                // set type to new value
                draft.contentType = initialFilterState.contentType;
                draft[IHubContentType.INSIGHTS].contentSubType =
                    isAccountAgnosticId()
                        ? defaultContentSubTypeForInsightInUniversalView
                        : defaultContentSubTypeForInsight;
                draft[IHubContentType.MARKETING_CONTENT].contentSubType =
                    defaultContentSubTypeForMarketingContent;
                return draft;
            });
        });
    },
    clearFieldSelections: (field: DiscoveryFilterField) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                draft[state.contentType].filters[
                    FieldToTypeMap[field]
                ].fieldSelections[field] = [];
                return draft;
            });
        });
    },
    setFieldSelections: (field: DiscoveryFilterField, items: Item[]) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                draft[state.contentType].filters[
                    FieldToTypeMap[field]
                ].fieldSelections[field] = items;
                return draft;
            });
        });
    },
    setField: (
        field: DiscoveryFilterField,
        item: Item,
        makeItVisible: boolean = false,
        allowMultiSelect: boolean = true,
    ) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                const currentFieldSelections = state[state.contentType].filters[
                    FieldToTypeMap[field]
                ].fieldSelections[field] as Item[];
                const fieldSelectionIndex = currentFieldSelections.findIndex(
                    (selection) =>
                        selection.value === item.value &&
                        selection.label === item.label,
                );

                if (fieldSelectionIndex >= 0) {
                    draft[state.contentType].filters[
                        FieldToTypeMap[field]
                    ].fieldSelections[field] = currentFieldSelections.filter(
                        (selection) =>
                            selection.value !== item.value ||
                            selection.label !== item.label,
                    );
                    return draft;
                }
                draft[state.contentType].filters[
                    FieldToTypeMap[field]
                ].fieldSelections[field] = allowMultiSelect
                    ? [...currentFieldSelections, item]
                    : [item];

                if (makeItVisible) {
                    const currentTypeFilters =
                        state[state.contentType].filters[FieldToTypeMap[field]];
                    const foundInAdditionalFilter =
                        currentTypeFilters.additionalFilters.find(
                            (f) => f.field === field,
                        );
                    const foundInVisibleFilter =
                        currentTypeFilters.visibleFilters.find(
                            (f) => f.field === field,
                        );
                    if (foundInAdditionalFilter && !foundInVisibleFilter) {
                        draft[state.contentType].filters[
                            FieldToTypeMap[field]
                        ].visibleFilters.push(foundInAdditionalFilter);
                    }
                }
                return draft;
            });
        });
    },
    getInsightsFilters: () => {
        const state: DiscoveryFilterState = get();
        return state.INSIGHTS.filters as DiscoveryInsightsFilters;
    },
    getMarketingContentFilters: () => {
        const state: DiscoveryFilterState = get();
        return state.MARKETING_CONTENT
            .filters as DiscoveryMarketingContentFilters;
    },
    getFilters: (contentType: IHubContentType | undefined) => {
        const state: DiscoveryFilterState = get();
        return state[contentType ?? state.contentType].filters;
    },
    setFilterOptions: (field: DiscoveryFilterField, items: Item[]) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                draft[state.contentType].filters[
                    FieldToTypeMap[field]
                ].filterOptions[field] = [...items];
                return draft;
            });
        });
    },
    // TODO: add type for data once we have each content type (Insights, Contents etc.)
    setData: (type: IHubContentProvider, data: any) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                if (draft[state.contentType].filters[type]) {
                    draft[state.contentType].filters[type].data = data;
                }
                return draft;
            });
        });
    },
    setSearchQuery: (query: string) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            const contentType = state.contentType;
            const provider = state[contentType].contentSubType.provider;
            return produce(state, (draft) => {
                draft[state.contentType].filters[provider].searchQuery = query;
                return draft;
            });
        });
    },
    resetSearchQueryAndSortAttribute: () => {
        set(() => {
            const state: DiscoveryFilterState = get();
            const contentType = state.contentType;
            const provider = state[contentType].contentSubType.provider;
            return produce(state, (draft) => {
                draft[state.contentType].filters[provider].searchQuery =
                    initialFilterState[state.contentType].filters[
                        provider
                    ].searchQuery;

                // Conditionally reset the sort attribute for MARKETING_NARRATIVES only
                if (provider === IHubContentProvider.MARKETING_NARRATIVES) {
                    draft[state.contentType].filters[
                        IHubContentProvider.MARKETING_NARRATIVES
                    ].sortAttribute = SearchSortAttribute.LAST_MODIFIED_DATE;
                }
                return draft;
            });
        });
    },
    addFilter: (filter: DiscoveryFilter) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                draft[state.contentType].filters[
                    FieldToTypeMap[filter.field]
                ].visibleFilters.push(filter);
                return draft;
            });
        });
    },
    removeFilter: (filter: DiscoveryFilter) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                // remove filter from visible filters list
                draft[state.contentType].filters[
                    FieldToTypeMap[filter.field]
                ].visibleFilters = draft[state.contentType].filters[
                    FieldToTypeMap[filter.field]
                ].visibleFilters.filter((f) => f.field !== filter.field);
                // reset filter field value to default value
                draft[state.contentType].filters[
                    FieldToTypeMap[filter.field]
                ].fieldSelections[filter.field] =
                    initialFilterState[state.contentType].filters[
                        FieldToTypeMap[filter.field]
                    ].fieldSelections[filter.field];
                return draft;
            });
        });
    },
    hasAppliedFilters: (contentSubType?: IHubContentSubType) => {
        const state: DiscoveryFilterState = get();
        const currentFilters = contentSubType
            ? {
                  [contentSubType.provider]:
                      state[state.contentType].filters[contentSubType.provider],
              }
            : state[state.contentType].filters;
        for (const [filterType, filterValue] of Object.entries(
            currentFilters ?? state[state.contentType].filters,
        )) {
            if (
                state[state.contentType].contentSubType.scope &&
                state[state.contentType].contentSubType.scope !== "All" &&
                state[state.contentType].contentSubType.scope !==
                    allPackagedInsightScope.value
            ) {
                return true;
            }

            // checks if any field values have been changed from the initial value
            for (const [field, fieldValue] of Object.entries(
                filterValue.fieldSelections,
            )) {
                if ((fieldValue as Item[]).length > 0) {
                    return true;
                }
            }

            // checks if visible filters have changed (additional filter has been applied)
            if (
                filterValue.visibleFilters.length !==
                initialFilterState[state.contentType].filters[filterType]
                    .visibleFilters.length
            )
                return true;
        }
        return false;
    },
    resetState: () => set(() => ({ ...initialFilterState })),
    resetFilters: () =>
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                const { provider } = draft[state.contentType].contentSubType;
                const { additionalFilters, fieldSelections, visibleFilters } =
                    initialFilterState[state.contentType].filters[provider];
                draft[state.contentType].filters[provider].fieldSelections =
                    fieldSelections;
                draft[state.contentType].filters[provider].visibleFilters =
                    visibleFilters;
                draft[state.contentType].filters[provider].additionalFilters =
                    additionalFilters;
                return draft;
            });
        }),
    setSortAttribute: (sortAttribute: string) =>
        set(() => {
            const state: DiscoveryFilterState = get();
            const provider = state[state.contentType].contentSubType.provider;
            return produce(state, (draft) => {
                if (provider === IHubContentProvider.MARKETING_NARRATIVES) {
                    draft[state.contentType].filters[
                        IHubContentProvider.MARKETING_NARRATIVES
                    ].sortAttribute = SearchSortAttribute[sortAttribute];
                }
                return draft;
            });
        }),
    setScopeToLabel: (scopeToLabel: { [key: string]: string }) => {
        set(() => {
            const state: DiscoveryFilterState = get();
            return produce(state, (draft) => {
                // set type to new value
                draft.scopeToLabel = scopeToLabel;
                return draft;
            });
        });
    },
}));
